import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/common";
import { todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";

export const EMPTY_TRUCK = { driver: "", truck_number: "", plate: "", size: "", cubic: "", lift_type: "", height: "", model: "", year: "", company: "", rego_expiry: "", fuel_card: "", fuel_card_number: "", last_service: "", next_service: "", notes: "" };
export const truckLabel = (t) => `${[t.truck_number, t.truck_number && t.plate ? "–" : "", t.plate].filter(Boolean).join(" ")}${t.size ? ` ${t.size}T` : ""}`.trim() || "Truck";

const COLS = [
  ["truck_number", "#", "w-14"], ["driver", "Driver", "w-36"], ["plate", "Plate", "w-24"], ["size", "Size (T)", "w-16"], ["cubic", "Cubic", "w-16"], ["lift_type", "Lift", "w-24"],
  ["height", "Height", "w-16"], ["model", "Model", "w-24"], ["year", "Year", "w-16"], ["company", "Company", "w-32"], ["rego_expiry", "Rego expiry", "w-36", "date"],
  ["fuel_card", "Fuel card", "w-20"], ["fuel_card_number", "Card no.", "w-24"], ["last_service", "Last service", "w-24"], ["next_service", "Next service", "w-24"],
];

export const FleetEditor = ({ draft, setDraft }) => {
  const fleet = draft.fleet || [];
  const today = todayISO();
  const update = (i, k, v) => setDraft({ ...draft, fleet: fleet.map((t, j) => (j === i ? { ...t, [k]: v } : t)) });
  const remove = (i) => window.confirm("Remove this truck?") && setDraft({ ...draft, fleet: fleet.filter((_, j) => j !== i) });
  const add = () => setDraft({ ...draft, fleet: [...fleet, { ...EMPTY_TRUCK, truck_number: String(fleet.length + 1) }] });
  const expired = fleet.filter((t) => t.rego_expiry && t.rego_expiry < today).length;
  return (
    <Panel title={`Trucks (${fleet.length})`} action={<Button size="sm" onClick={add} data-testid="fleet-add" className="bg-[#0E1E38] text-white hover:bg-[#1a3058]"><Plus className="h-4 w-4" /> Add truck</Button>}>
      <p className="mb-3 text-xs text-slate-500">Trucks appear in the booking "Truck assigned" dropdown as "number – plate size". Rows highlighted red have an <b>expired rego</b>{expired ? ` (${expired} now)` : ""}; amber = due within 30 days. Press Save (top right) after editing.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="fleet-table">
          <thead><tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">{COLS.map(([k, h]) => <th key={k} className="whitespace-nowrap px-1 pb-2">{h}</th>)}<th /></tr></thead>
          <tbody>
            {fleet.map((t, i) => {
              const soon = t.rego_expiry && t.rego_expiry >= today && t.rego_expiry <= new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
              const isExpired = t.rego_expiry && t.rego_expiry < today;
              return (
                <tr key={i} className={cn("border-t border-slate-100", isExpired && "bg-rose-50", soon && "bg-amber-50")} data-testid={`fleet-row-${i}`}>
                  {COLS.map(([k, , w, type]) => (
                    <td key={k} className="px-1 py-1">
                      <div className="relative">
                        <Input type={type || "text"} value={t[k] ?? ""} onChange={(e) => update(i, k, e.target.value)} className={cn("h-8 bg-white px-2 text-xs", w, k === "rego_expiry" && isExpired && "border-rose-400 font-semibold text-rose-700")} data-testid={`fleet-${k}-${i}`} />
                        {k === "rego_expiry" && isExpired && <AlertTriangle className="pointer-events-none absolute -left-4 top-2 h-3.5 w-3.5 text-rose-600" />}
                      </div>
                    </td>
                  ))}
                  <td className="px-1 py-1"><button onClick={() => remove(i)} className="rounded p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`fleet-remove-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};
